import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTeamStore } from '../../lib/store/teamStore';
import Card from '../common/Card';
import Button from '../common/Button';
import { Dialog } from '../common/Dialog';
import Alert from '../common/Alert';
import { ShiftTypeSelector } from './ShiftTypeSelector';
import { ShiftTypeBadge } from './ShiftTypeBadge';

export const BulkShiftTypeUpdate = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { teamMembers, updateTeamMember } = useTeamStore();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectedShiftType, setSelectedShiftType] = useState<'days_only' | 'nights_only' | 'both'>('both');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Reset state when dialog closes
  const handleClose = () => {
    setIsDialogOpen(false);
    setSelectedMembers([]);
    setSelectedShiftType('both');
    setError(null);
  };
  
  // Toggle selection of a member
  const toggleMemberSelection = (userId: string) => {
    setSelectedMembers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  // Select all members
  const selectAllMembers = () => {
    setSelectedMembers(teamMembers.map(member => member.user_id));
  };
  
  // Deselect all members
  const deselectAllMembers = () => {
    setSelectedMembers([]);
  };
  
  // Apply shift type change to selected members
  const applyShiftTypeChange = async () => {
    if (!teamId || selectedMembers.length === 0) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Update each selected member one by one
      for (const userId of selectedMembers) {
        await updateTeamMember({
          team_id: teamId,
          user_id: userId,
          shift_type: selectedShiftType
        });
      }
      
      setSuccessMessage(`Updated shift type for ${selectedMembers.length} team members`);
      handleClose();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update shift types. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}
      
      <Card className="p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Shift Type Management</h2>
            <p className="text-gray-600 mt-1">Update shift types for multiple team members at once</p>
          </div>
          <Button
            variant="primary"
            onClick={() => setIsDialogOpen(true)}
            className="mt-3 sm:mt-0"
          >
            Bulk Update Shift Types
          </Button>
        </div>
      </Card>
      
      <Dialog
        isOpen={isDialogOpen}
        onClose={handleClose}
        title="Bulk Update Shift Types"
      >
        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}
        
        <div className="mb-4">
          <p className="text-gray-600">
            Select team members and choose the shift type to apply to all selected members.
          </p>
        </div>
        
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium">Team Members</h3>
            <div className="space-x-2">
              <Button
                variant="text"
                size="sm"
                onClick={selectAllMembers}
              >
                Select All
              </Button>
              <Button
                variant="text"
                size="sm"
                onClick={deselectAllMembers}
              >
                Deselect All
              </Button>
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto border rounded-md">
            {teamMembers.map(member => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.user_id)}
                    onChange={() => toggleMemberSelection(member.user_id)}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div className="ml-3">
                    <div className="font-medium">{member.user.full_name}</div>
                    <div className="text-sm text-gray-500">{member.user.email}</div>
                  </div>
                </div>
                <ShiftTypeBadge type={member.shift_type} />
              </div>
            ))}
          </div>
        </div>
        
        <div className="mb-6">
          <h3 className="font-medium mb-2">New Shift Type</h3>
          <ShiftTypeSelector
            value={selectedShiftType}
            onChange={setSelectedShiftType}
            disabled={isSubmitting}
          />
        </div>
        
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={applyShiftTypeChange}
            isLoading={isSubmitting}
            disabled={selectedMembers.length === 0}
          >
            Apply to {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </Dialog>
    </>
  );
}; 